<template>
  <div class="pc-checkout">
    <div v-if="!items.length" class="pc-container empty-checkout">
      <h2>暂无待结算商品</h2>
      <button @click="$router.push('/pc/cart')">返回购物车</button>
    </div>

    <div v-else class="pc-container checkout-content">
      <section class="checkout-card delivery-card">
        <header><h2>配送信息</h2><button @click="openAddressModal(false)">使用新地址</button></header>
        <div class="delivery-tabs">
          <button :class="{ active: deliveryType === 'express' }" @click="deliveryType = 'express'">✓ 快递寄送</button>
          <button :class="{ active: deliveryType === 'pickup' }" @click="deliveryType = 'pickup'">上门自提</button>
        </div>

        <div v-if="deliveryType === 'express'" class="delivery-body">
          <div v-if="address" class="address-card" @click="openAddressModal(true)">
            <div><span v-if="address.isDefault">默认</span><b>{{ address.receiver }}</b> {{ address.phone }}</div>
            <p>{{ address.region }} {{ address.detail }}</p>
            <button>编辑地址</button>
          </div>
          <button v-else class="empty-address" @click="openAddressModal(false)">＋ 添加收货地址</button>
          <div class="delivery-divider"></div>
          <div class="choice-row">
            <span>快递选择：</span>
            <select v-model.number="selectedExpressCompanyId" aria-label="选择快递">
              <option v-for="item in expressOptions" :key="item.id" :value="item.id">{{ expressRuleText(item) }}</option>
            </select>
          </div>
          <div class="radio-row">
            <span>运费方式：</span>
            <label><input v-model="freightType" type="radio" value="寄付"> 运费寄付</label>
            <label><input v-model="freightType" type="radio" value="到付"> 运费到付</label>
          </div>
          <p class="freight-tip">{{ freightType === '到付' ? '运费到付，本单不收取运费' : `当前运费 ¥${freightAmount}，按地区、基础重量与续重规则计算` }}</p>
        </div>
        <div v-else class="delivery-body pickup-body">
          <h3>上门自提信息</h3>
          <p><span>自提地址：</span>{{ pickupAddress || '请联系客服确认自提地址' }}</p>
          <p><span>联系人：</span>{{ pickupContact || companyName }}</p>
          <p><span>联系电话：</span>{{ pickupPhone || servicePhone }}</p>
          <p><span>自提时间：</span>{{ pickupHours || '请提前联系客服预约' }}</p>
        </div>
      </section>

      <section class="checkout-card invoice-card">
        <header><h2>开票信息</h2><span>发票须知</span></header>
        <div class="invoice-tabs">
          <button :class="{ active: invoiceMode === 'company' }" @click="selectInvoiceMode('company')">公司发票</button>
          <button :class="{ active: invoiceMode === 'personal' }" @click="selectInvoiceMode('personal')">个人发票</button>
          <button :class="{ active: invoiceMode === 'none' }" @click="selectInvoiceMode('none')">✓ 不需要发票</button>
        </div>
        <div class="invoice-content">
          <span v-if="invoiceMode === 'none'">不需要发票</span>
          <template v-else>
            <span>{{ invoiceMode === 'company' ? '公司发票' : '个人发票' }}：{{ invoiceForm.title || '尚未填写' }}</span>
            <button @click="openInvoiceModal">{{ invoiceForm.title ? '编辑发票' : '填写发票' }}</button>
          </template>
        </div>
      </section>

      <section class="checkout-card goods-card">
        <header>
          <h2>商品清单 <small>请确认商品型号、封装及数量</small></h2>
          <button @click="$router.push('/pc/cart')">返回购物车修改</button>
        </header>
        <div class="goods-table">
          <div class="goods-table-head goods-columns">
            <span>型号</span><span>品牌</span><span>封装</span><span>批次</span><span>货期</span><span>单价（含税）</span><span>数量</span><span>小计</span>
          </div>
          <div v-for="item in items" :key="item.id" class="goods-table-row goods-columns">
            <span class="goods-model">
              <strong>{{ productTitle(item) }}</strong>
              <small v-if="productType(item)">产品类型：{{ productType(item) }}</small>
            </span>
            <span>{{ productBrand(item) }}</span>
            <span>{{ item.package }}</span>
            <span>{{ item.batch || '--' }}</span>
            <span>现货，尽快发货</span>
            <span>¥ {{ itemUnitPrice(item) }}</span>
            <span>{{ item.quantity }}</span>
            <b>¥ {{ itemAmount(item) }}</b>
          </div>
        </div>
      </section>

      <section class="checkout-card settings-card">
        <header><h2>个性化设置</h2></header>
        <label><span>订单备注：</span><input v-model="remark" maxlength="70" placeholder="可输入70字以内的备注"></label>
        <label><span>合同号：</span><input v-model="contractNo" maxlength="60" placeholder="请输入客户合同号"></label>
      </section>

      <section class="checkout-card coupon-card">
        <header><h2>使用优惠券</h2></header>
        <div v-if="!coupons.length" class="no-coupon">当前订单暂无可用优惠券</div>
        <div v-else class="coupon-options">
          <button :class="{ active: !selectedCouponId }" @click="selectedCouponId = null">不使用优惠券</button>
          <button v-for="item in coupons" :key="item.id" :class="{ active: selectedCouponId === item.id }"
            @click="selectedCouponId = item.id">{{ couponLabel(item) }}</button>
        </div>
      </section>

      <section class="checkout-card order-summary">
        <div class="summary-top">已选中 <b>{{ items.length }}</b> 种商品</div>
        <div class="summary-body">
          <div class="agreements">
            <label><input v-model="agreementChecked" type="checkbox"> 我已阅读并同意{{ companyName }}《订单提交协议》</label>
            <label><input v-model="promiseChecked" type="checkbox"> 我已阅读并承诺遵守采购《进出口合规承诺书》</label>
          </div>
          <div class="amount-list">
            <p><span>商品总金额：</span><b>¥ {{ productAmount }}</b></p>
            <p><span>运费：</span><b>¥ {{ freightAmount }}</b></p>
            <p><span>优惠总金额：</span><b>- ¥ {{ discountAmount }}</b></p>
            <p class="payable"><span>应付金额：</span><strong>¥ {{ payableAmount }}</strong></p>
            <button ref="submitButton" class="submit-button" :disabled="submitting" @click="submitOrder">{{ submitting ? '提交中...' : '提交订单' }}</button>
          </div>
        </div>
      </section>
    </div>

    <div v-if="showFloatingBar && items.length" class="floating-submit">
      <div class="floating-inner pc-container">
        <div class="floating-agreements">
          <label><input v-model="agreementChecked" type="checkbox"> 同意《订单提交协议》</label>
          <label><input v-model="promiseChecked" type="checkbox"> 同意《进出口合规承诺书》</label>
        </div>
        <div class="floating-total">已选 <b>{{ items.length }}</b> 种商品，应付金额：<strong>¥ {{ payableAmount }}</strong></div>
        <button :disabled="submitting" @click="submitOrder">{{ submitting ? '提交中...' : '提交订单' }}</button>
      </div>
    </div>

    <div v-if="addressModalVisible" class="modal-mask" @click.self="addressModalVisible = false">
      <div class="form-modal">
        <button class="modal-close" @click="addressModalVisible = false">×</button>
        <h2>{{ editingAddress ? '修改收货地址' : '新增收货地址' }}</h2>
        <label><span><i>*</i> 收货人：</span><input v-model.trim="addressForm.receiver"></label>
        <label><span><i>*</i> 手机号码：</span><input v-model.trim="addressForm.phone" maxlength="20"></label>
        <label><span><i>*</i> 地址选择：</span><china-address-cascader v-model="addressForm.region" /></label>
        <label><span><i>*</i> 详细地址：</span><input v-model.trim="addressForm.detail" placeholder="街道、楼栋、门牌号"></label>
        <label class="default-line"><span>默认地址：</span><input v-model="addressForm.isDefault" type="checkbox"> 设为默认</label>
        <div class="modal-actions"><button @click="addressModalVisible = false">取消</button><button class="primary" @click="saveAddress">确定</button></div>
      </div>
    </div>

    <div v-if="invoiceModalVisible" class="modal-mask" @click.self="invoiceModalVisible = false">
      <div class="form-modal invoice-modal member-invoice-modal">
        <button class="modal-close" @click="invoiceModalVisible = false">×</button>
        <h2>{{ invoiceMode === 'company' ? '填写公司发票' : '填写个人发票' }}</h2>
        <p class="invoice-warning">温馨提示：请仔细填写开票信息，避免错误信息导致发票无法使用。</p>
        <label v-if="invoiceMode === 'company'"><span><i>*</i> 发票类型：</span><span class="invoice-type-options"><label><input v-model="invoiceCategory" type="radio" value="普通发票"> 普通发票</label><label><input v-model="invoiceCategory" type="radio" value="专用发票"> 专用发票</label></span></label>
        <label v-else><span><i>*</i> 发票类型：</span><span class="radio-text">数电普票</span></label>
        <label><span><i>*</i> 发票抬头：</span><input v-model.trim="invoiceForm.title" :placeholder="invoiceMode === 'company' ? '请输入公司名称' : '请输入个人姓名'"></label>
        <label v-if="invoiceMode === 'company'"><span><i>*</i> 税务登记号：</span><input v-model.trim="invoiceForm.taxNo"></label>
        <label class="default-line"><span>默认发票：</span><input v-model="invoiceForm.isDefault" type="checkbox"> 设为默认</label>
        <div class="modal-actions"><button @click="invoiceModalVisible = false">取消</button><button class="primary" @click="saveInvoice">确定</button></div>
      </div>
    </div>

    <div v-if="paymentVisible" class="modal-mask" @click.self="closePayment">
      <div class="form-modal payment-modal">
        <button class="modal-close" @click="closePayment">×</button>
        <h2>订单提交成功</h2>
        <p>订单号：{{ payment.orderNo }}</p>
        <div class="qr-placeholder"><img v-if="qrDataUrl" :src="qrDataUrl" alt="微信支付二维码"><span v-else>微信支付二维码生成中</span></div>
        <p>{{ paymentStatusText }}</p>
        <div class="modal-actions"><button @click="exportPendingContract">导出合同</button><button @click="checkPaymentNow">我已完成支付</button><button class="primary" @click="refreshPaymentQr">刷新支付码</button></div>
      </div>
    </div>
  </div>
</template>

<script>
  import { message } from 'ant-design-vue'
  import {
    createPcNativePayment, createPcOrder, getPcOrderDetail, getPcMallSettings, previewPcOrder,
    getPcMemberAddresses, savePcMemberAddress, getPcMemberInvoiceProfiles, getPcMemberCoupons, getPcExpressOptions,
    downloadPcOrderContract
  } from '@/api/pc'
  import { downloadBlobResponse } from '@/utils/downloadFile'
  import QRCode from 'qrcode'
  import commerce from '@/utils/pcCommerce'
  import { productTitle, productType, productBrand } from '@/utils/pcProductDisplay'
  import { getCartItems, saveCartItems } from '@/utils/pcCart'
  import ChinaAddressCascader from '@/components/pc/ChinaAddressCascader.vue'

  export default {
    name: 'PcCheckout',
    components: { ChinaAddressCascader },
    data() {
      return {
        items: [],
        companyName: '锐高科技',
        servicePhone: '400-862-6630',
        pickupAddress: '',
        pickupContact: '',
        pickupPhone: '',
        pickupHours: '',
        deliveryType: 'express',
        expressOptions: [],
        selectedExpressCompanyId: null,
        freightType: '寄付',
        address: null,
        addressForm: this.emptyAddress(),
        addressModalVisible: false,
        editingAddress: false,
        invoiceMode: 'none',
        invoiceCategory: '普通发票',
        invoiceForm: this.emptyInvoice(),
        invoiceProfiles: [],
        invoiceModalVisible: false,
        remark: '',
        contractNo: '',
        agreementChecked: true,
        promiseChecked: true,
        submitting: false,
        showFloatingBar: false,
        submitObserver: null,
        paymentVisible: false,
        payment: {},
        paymentStatusText: '',
        pollTimer: null,
        pollAttempts: 0,
        pendingOrder: null,
        clientRequestId: `pc-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`,
        coupons: [],
        selectedCouponId: null,
        amountPreview: null,
        qrDataUrl: ''
      }
    },
    computed: {
      productAmount() {
        const total = this.items.reduce((sum, item) => sum + Number(this.itemAmount(item)), 0)
        return commerce.formatMoney(total, 2)
      },
      freightAmount() { return this.amountPreview ? commerce.formatMoney(this.amountPreview.freightAmount, 2) : '0.00' },
      discountAmount() { return this.amountPreview ? commerce.formatMoney(this.amountPreview.discountAmount, 2) : '0.00' },
      payableAmount() { return this.amountPreview ? commerce.formatMoney(this.amountPreview.payAmount, 2) : this.productAmount }
    },
    watch: {
      deliveryType() { this.refreshPreview() },
      selectedExpressCompanyId() { this.refreshPreview() },
      freightType() { this.refreshPreview() },
      selectedCouponId() { this.refreshPreview() }
    },
    created() {
      if (!localStorage.getItem('mall_access_token')) {
        this.$router.replace({ path: '/pc/login', query: { redirect: '/pc/checkout' } })
        return
      }
      this.items = getCartItems().filter(item => item.checked)
      this.restorePendingOrder()
      getPcMallSettings().then(data => {
        const setting = data || {}
        this.companyName = setting.companyName || this.companyName
        this.servicePhone = setting.servicePhone || this.servicePhone
        this.pickupAddress = setting.pickupAddress || setting.companyAddress || ''
        this.pickupContact = setting.pickupContact || ''
        this.pickupPhone = setting.pickupPhone || ''
        this.pickupHours = setting.pickupHours || ''
      }).catch(() => {})
      this.loadDefaultAddress()
      this.loadExpressOptions()
      this.loadInvoiceProfiles()
      this.loadCoupons()
    },
    mounted() {
      this.$nextTick(this.observeSubmitButton)
    },
    beforeDestroy() {
      if (this.submitObserver) this.submitObserver.disconnect()
      this.stopPolling()
    },
    methods: {
      couponLabel(item) { return item.couponType === '包邮券' ? `${item.name}（最高抵扣运费${item.discountAmount}元）` : `${item.name}（满${item.thresholdAmount}减${item.discountAmount}）` },
      productTitle,
      productType,
      productBrand,
      loadExpressOptions() {
        getPcExpressOptions().then(rows => {
          this.expressOptions = Array.isArray(rows) ? rows : []
          const selected = this.expressOptions.find(item => item.id === this.selectedExpressCompanyId)
          const fallback = this.expressOptions.find(item => item.isDefault === 1) || this.expressOptions[0]
          this.selectedExpressCompanyId = (selected || fallback || {}).id || null
        }).catch(() => { this.expressOptions = [] })
      },
      loadCoupons() {
        getPcMemberCoupons('未使用').then(data => { this.coupons = data || []; this.refreshPreview() }).catch(() => { this.coupons = [] })
      },
      refreshPreview() {
        if (!this.items.length) return
        const address = this.address || {}
        previewPcOrder({
          deliveryType: this.deliveryType,
          freightPaymentType: this.freightType,
          expressCompanyId: this.selectedExpressCompanyId,
          receiverAddress: address.region ? `${address.region} ${address.detail}` : '',
          couponUserId: this.selectedCouponId,
          items: this.items.map(item => ({ productId: item.productId || item.id, quantity: item.quantity }))
        }).then(data => { this.amountPreview = data || null }).catch(error => { message.error(error.message || '结算金额计算失败') })
      },
      emptyAddress() { return { id: null, receiver: '', phone: '', region: '', detail: '', isDefault: true } },
      emptyInvoice() { return { title: '', taxNo: '', isDefault: true } },
      itemUnitPrice(item) { return commerce.formatMoney(commerce.getTierPrice(item, item.quantity), 4) },
      expressRuleText(item) {
        return `${item.name}（${Number(item.baseWeightKg || 1)}kg内￥${commerce.formatMoney(item.baseFee, 2)}，续重￥${commerce.formatMoney(item.additionalFeePerKg, 2)}/kg）`
      },
      itemAmount(item) { return commerce.formatMoney(commerce.getTierPrice(item, item.quantity) * Number(item.quantity || 0), 2) },
      openAddressModal(editing) {
        this.editingAddress = Boolean(editing && this.address)
        this.addressForm = this.editingAddress ? { ...this.address } : this.emptyAddress()
        this.addressModalVisible = true
      },
      loadDefaultAddress() {
        if (!localStorage.getItem('mall_access_token')) return
        getPcMemberAddresses().then(rows => {
          const item = (rows || []).find(row => row.isDefault === 1) || (rows || [])[0]
           if (item) this.address = { id: item.id, receiver: item.receiverName, phone: item.receiverPhone, region: item.region, detail: item.detailAddress, isDefault: item.isDefault === 1 }
           this.refreshPreview()
        }).catch(() => {})
      },
      loadInvoiceProfiles() {
        if (!localStorage.getItem('mall_access_token')) return
        getPcMemberInvoiceProfiles().then(data => {
          this.invoiceProfiles = Array.isArray(data && data.profiles) ? data.profiles : []
          const defaultProfile = this.invoiceProfiles.find(item => item.isDefault === 1) || this.invoiceProfiles[0]
          if (defaultProfile) this.applyInvoiceProfile(defaultProfile)
        }).catch(() => {})
      },
      applyInvoiceProfile(profile) {
        if (!profile) return
        this.invoiceMode = profile.profileType === 'company' ? 'company' : 'personal'
        this.invoiceForm = {
          title: profile.invoiceTitle || '',
          taxNo: profile.taxNo || '',
          isDefault: profile.isDefault === 1
        }
      },
      async saveAddress() {
        const form = this.addressForm
        if (!form.receiver || !form.phone || !form.region || !form.detail) return message.warning('请完整填写收货地址')
        if (!/^1\d{10}$/.test(form.phone) && !/^\+?[\d\s-]{6,20}$/.test(form.phone)) return message.warning('请输入正确的手机号码')
        if (!localStorage.getItem('mall_access_token')) {
          this.address = { ...form }
          this.addressModalVisible = false
          return
        }
        try {
          const saved = await savePcMemberAddress({ id: form.id, receiverName: form.receiver, receiverPhone: form.phone, region: form.region, detailAddress: form.detail, isDefault: form.isDefault })
          this.address = { id: saved.id, receiver: saved.receiverName, phone: saved.receiverPhone, region: saved.region, detail: saved.detailAddress, isDefault: saved.isDefault === 1 }
          this.addressModalVisible = false
          message.success('收货地址保存成功')
          this.refreshPreview()
        } catch (error) {
          message.error(error.message || '收货地址保存失败')
        }
      },
      selectInvoiceMode(mode) {
        this.invoiceMode = mode
        if (mode === 'none') {
          this.invoiceModalVisible = false
          return
        }
        const matched = this.invoiceProfiles.find(item => item.profileType === mode && item.isDefault === 1)
          || this.invoiceProfiles.find(item => item.profileType === mode)
        this.invoiceForm = matched
          ? { title: matched.invoiceTitle || '', taxNo: matched.taxNo || '', isDefault: matched.isDefault === 1 }
          : this.emptyInvoice()
      },
      openInvoiceModal() { this.invoiceModalVisible = true },
      saveInvoice() {
        if (!this.invoiceForm.title) return message.warning('请填写发票抬头')
        if (this.invoiceMode === 'company' && !this.invoiceForm.taxNo) return message.warning('请填写税务登记号')
        this.invoiceModalVisible = false
      },
      observeSubmitButton() {
        if (!this.$refs.submitButton || typeof IntersectionObserver === 'undefined') return
        this.submitObserver = new IntersectionObserver(entries => {
          this.showFloatingBar = !entries[0].isIntersecting
        }, { threshold: 0.05 })
        this.submitObserver.observe(this.$refs.submitButton)
      },
      async submitOrder() {
        if (this.submitting) return
        if (this.deliveryType === 'express' && !this.address) return message.warning('请先添加收货地址')
        if (this.deliveryType === 'express' && !this.selectedExpressCompanyId) return message.warning('暂无可用快递，请联系管理员')
        if (this.invoiceMode !== 'none' && !this.invoiceForm.title) return message.warning('请完善发票信息')
        if (!this.agreementChecked || !this.promiseChecked) return message.warning('请阅读并同意采购协议与合规承诺书')
        this.submitting = true
        try {
          if (this.pendingOrder && this.pendingOrder.orderNo) {
            await this.openPayment(this.pendingOrder)
            return
          }
          const address = this.address || {}
          const selectedExpress = this.expressOptions.find(item => item.id === this.selectedExpressCompanyId)
          const remarkParts = [this.remark, this.contractNo ? `合同号：${this.contractNo}` : '', `配送：${this.deliveryType === 'express' ? ((selectedExpress && selectedExpress.name) || '默认快递') + '（运费' + this.freightType + '）' : '上门自提'}`].filter(Boolean)
          const order = await createPcOrder({
            customerName: address.receiver || '默认客户',
            customerPhone: address.phone || '',
            receiverName: address.receiver || '',
            receiverPhone: address.phone || '',
            receiverAddress: address.region ? `${address.region} ${address.detail}` : '',
            deliveryType: this.deliveryType,
            freightPaymentType: this.freightType,
            expressCompanyId: this.selectedExpressCompanyId,
            couponUserId: this.selectedCouponId,
            remark: remarkParts.join('；'),
            invoiceType: this.invoiceMode,
            invoiceCategory: this.invoiceMode === 'company' ? this.invoiceCategory : '普通发票',
            invoiceTitle: this.invoiceForm.title,
            taxNo: this.invoiceForm.taxNo,
            clientRequestId: this.clientRequestId,
            items: this.items.map(item => ({ productId: item.productId || item.id, quantity: item.quantity }))
          })
          this.pendingOrder = order
          this.cachePendingOrder()
          await this.openPayment(order)
        } catch (error) {
          message.error(error.message || '订单提交失败')
        } finally {
          this.submitting = false
        }
      },
      async openPayment(order) {
        const payment = await createPcNativePayment({ orderNo: order.orderNo })
          this.payment = { ...payment, orderNo: order.orderNo }
          this.qrDataUrl = await QRCode.toDataURL(payment.codeUrl, { width: 220, margin: 1, errorCorrectionLevel: 'M' })
          this.paymentVisible = true
          this.paymentStatusText = '请使用微信扫码支付，支付完成后页面会自动确认'
          this.startPolling(order.orderNo)
      },
      startPolling(orderNo) {
        this.stopPolling()
        this.pollAttempts = 0
        const poll = async () => {
          this.pollAttempts += 1
          try {
            const detail = await getPcOrderDetail(orderNo)
            const order = detail.order || detail
            if (this.handlePaymentResult(order)) return
          } catch (e) {
            // 轮询异常时保留当前支付页面，等待下一次查询。
          }
          if (this.pollAttempts >= 200) {
            this.paymentStatusText = '自动查询已暂停，请点击“我已完成支付”重新确认'
            return
          }
          this.pollTimer = window.setTimeout(poll, 3000)
        }
        this.pollTimer = window.setTimeout(poll, 3000)
      },
      stopPolling() {
        if (this.pollTimer) window.clearTimeout(this.pollTimer)
        this.pollTimer = null
      },
      handlePaymentResult(order) {
        if (order.payStatus === '已支付') {
          this.stopPolling()
          this.paymentStatusText = '支付成功'
          const selectedIds = this.items.map(item => String(item.id))
          saveCartItems(getCartItems().filter(item => selectedIds.indexOf(String(item.id)) === -1))
          this.pendingOrder = null
          this.clearPendingOrder()
          this.$emit('cart-change')
          message.success('支付成功')
          return true
        }
        if (['已取消', '已关闭'].indexOf(order.orderStatus) >= 0) {
          this.stopPolling()
          this.paymentStatusText = `订单${order.orderStatus}，无法继续支付`
          this.pendingOrder = null
          this.clearPendingOrder()
          return true
        }
        return false
      },
      async checkPaymentNow() {
        if (!this.payment.orderNo) return
        try {
          const detail = await getPcOrderDetail(this.payment.orderNo)
          const order = detail.order || detail
          if (!this.handlePaymentResult(order)) {
            this.paymentStatusText = '暂未确认到账，请稍候再试'
            this.startPolling(this.payment.orderNo)
          }
        } catch (error) {
          message.error(error.message || '支付结果查询失败')
        }
      },
      async exportPendingContract() {
        if (!this.payment.orderNo) return
        try {
          const response = await downloadPcOrderContract(this.payment.orderNo)
          await downloadBlobResponse(response, `销售合同-${this.payment.orderNo}.pdf`)
        } catch (error) {
          message.error(error.message || '合同导出失败')
        }
      },
      async refreshPaymentQr() {
        if (!this.pendingOrder) return
        this.submitting = true
        try {
          await this.openPayment(this.pendingOrder)
        } catch (error) {
          message.error(error.message || '支付码刷新失败')
        } finally {
          this.submitting = false
        }
      },
      closePayment() {
        this.paymentVisible = false
        this.stopPolling()
      },
      cachePendingOrder() {
        if (!this.pendingOrder) return
        sessionStorage.setItem('__pc_pending_payment_order_v1__', JSON.stringify({
          order: this.pendingOrder,
          clientRequestId: this.clientRequestId,
          productIds: this.items.map(item => String(item.productId || item.id)).sort(),
          createdAt: Date.now()
        }))
      },
      restorePendingOrder() {
        try {
          const cached = JSON.parse(sessionStorage.getItem('__pc_pending_payment_order_v1__') || 'null')
          const currentIds = this.items.map(item => String(item.productId || item.id)).sort()
          if (!cached || Date.now() - Number(cached.createdAt || 0) > 60 * 60 * 1000
              || JSON.stringify(cached.productIds || []) !== JSON.stringify(currentIds)) {
            this.clearPendingOrder()
            return
          }
          this.pendingOrder = cached.order || null
          this.clientRequestId = cached.clientRequestId || this.clientRequestId
        } catch (error) {
          this.clearPendingOrder()
        }
      },
      clearPendingOrder() {
        sessionStorage.removeItem('__pc_pending_payment_order_v1__')
      }
    }
  }
</script>

<style scoped>
  .pc-checkout { min-height: calc(100vh - 176px); padding: 14px 0 100px; background: #f3f5f9; color: #334155; }
  .checkout-content { display: grid; gap: 12px; }
  .checkout-card { padding: 18px; border-radius: 10px; background: #fff; box-sizing: border-box; }
  .checkout-card header { min-height: 34px; display: flex; align-items: center; justify-content: space-between; }
  .checkout-card h2 { margin: 0; color: #111827; font-size: 17px; }
  .checkout-card h2 small { margin-left: 10px; color: #64748b; font-size: 13px; font-weight: 400; }
  .checkout-card header button, .checkout-card header span { border: 0; background: transparent; color: #1684ff; cursor: pointer; }
  input[type='checkbox'], input[type='radio'] { accent-color: #1684ff; }
  .delivery-tabs, .invoice-tabs { margin-top: 6px; display: flex; }
  .delivery-tabs button, .invoice-tabs button, .choice-row button { min-width: 116px; height: 40px; padding: 0 18px; border: 1px solid #d7dde7; background: #fff; color: #475569; cursor: pointer; }
  .delivery-tabs button:first-child, .invoice-tabs button:first-child { border-radius: 6px 0 0 0; }
  .delivery-tabs button.active, .invoice-tabs button.active, .choice-row button.active { border-color: #1684ff; color: #1684ff; background: #f2f8ff; }
  .delivery-body, .invoice-content { min-height: 160px; padding: 16px; border: 1px solid #dfe4ec; border-radius: 0 6px 6px 6px; box-sizing: border-box; }
  .address-card { position: relative; width: 340px; padding: 14px; border: 1px solid #1684ff; border-radius: 7px; cursor: pointer; box-sizing: border-box; }
  .address-card span { margin-right: 8px; padding: 2px 5px; background: #1684ff; color: #fff; border-radius: 3px; }
  .address-card b { margin-right: 12px; }
  .address-card p { margin: 9px 0 0; }
  .address-card button { position: absolute; right: 10px; top: 10px; border: 0; background: transparent; color: #1684ff; }
  .empty-address { width: 230px; height: 74px; border: 1px dashed #1684ff; border-radius: 6px; background: #f8fbff; color: #1684ff; cursor: pointer; }
  .delivery-divider { margin: 16px 0; border-top: 1px dashed #dfe4ec; }
  .choice-row, .radio-row { margin-top: 10px; display: flex; align-items: center; gap: 12px; }
  .choice-row select { min-width: 220px; height: 36px; padding: 0 10px; border: 1px solid #d7dde7; border-radius: 5px; background: #fff; color: #334155; outline-color: #1684ff; }
  .choice-row button { min-width: 110px; height: 34px; border-radius: 5px; }
  .radio-row label { margin-right: 18px; }
  .freight-tip { color: #ff4d00; }
  .pickup-body { color: #64748b; }
  .pickup-body h3 { margin: 0 0 12px; color: #1f2937; font-size: 15px; }
  .pickup-body p { margin: 7px 0; }
  .pickup-body p span { display: inline-block; width: 82px; color: #475569; }
  .invoice-content { min-height: 54px; display: flex; align-items: center; justify-content: space-between; }
  .invoice-content button { border: 0; background: transparent; color: #1684ff; cursor: pointer; }
  .goods-table { margin-top: 10px; border: 1px solid #dfe4ec; border-radius: 6px; overflow: hidden; }
  .goods-columns { display: grid; grid-template-columns: 1.35fr 1fr 1fr .7fr 1fr 1fr .65fr .85fr; align-items: center; }
  .goods-table-head { min-height: 40px; padding: 0 10px; background: #f6f8fb; color: #1f2937; font-weight: 700; }
  .goods-table-row { min-height: 58px; padding: 0 10px; border-top: 1px solid #e5e7eb; }
  .goods-table-row span { padding-right: 8px; word-break: break-word; }
  .goods-model { padding-top: 10px; padding-bottom: 10px; }
  .goods-model strong, .goods-model small { display: block; }
  .goods-model small { margin-top: 4px; color: #667085; }
  .goods-table-row b { color: #ff4d00; }
  .settings-card label { margin-top: 10px; display: grid; grid-template-columns: 70px 1fr; align-items: center; }
  .settings-card input { height: 34px; padding: 0 10px; border: 1px solid #d7dde7; border-radius: 5px; outline-color: #1684ff; }
  .no-coupon { margin-top: 8px; padding: 24px; border: 1px dashed #d7dde7; color: #94a3b8; text-align: center; }
  .summary-top { padding-bottom: 12px; border-bottom: 1px solid #e5e7eb; }
  .summary-top b { color: #ff4d00; }
  .summary-body { padding-top: 14px; display: flex; justify-content: space-between; align-items: flex-end; }
  .agreements { display: grid; gap: 8px; }
  .amount-list { width: 400px; }
  .amount-list p { margin: 8px 0; display: flex; justify-content: space-between; }
  .amount-list b { color: #1f2937; }
  .amount-list .payable { margin-top: 18px; font-size: 17px; font-weight: 700; }
  .payable strong { color: #ff4d00; font-size: 24px; }
  .submit-button, .floating-submit button, .empty-checkout button { float: right; width: 210px; height: 46px; border: 0; border-radius: 24px; background: #1684ff; color: #fff; font-size: 18px; font-weight: 700; cursor: pointer; }
  .submit-button:disabled, .floating-submit button:disabled { background: #aeb8c5; }
  .floating-submit { position: fixed; z-index: 30; left: 0; right: 3px; bottom: 0; min-height: 76px; border-top: 1px solid #d7dde7; background: #fff; box-shadow: 0 -5px 18px rgba(15, 23, 42, .1); }
  .floating-inner { min-height: 76px; display: flex; align-items: center; justify-content: flex-end; gap: 28px; }
  .floating-agreements { display: grid; gap: 4px; font-size: 12px; }
  .floating-total b, .floating-total strong { color: #ff4d00; }
  .floating-total strong { font-size: 22px; }
  .modal-mask { position: fixed; z-index: 60; inset: 0; display: flex; align-items: center; justify-content: center; background: rgba(15, 23, 42, .46); }
  .form-modal { position: relative; width: 560px; padding: 26px; border-radius: 14px; background: #fff; box-shadow: 0 20px 50px rgba(0, 0, 0, .2); box-sizing: border-box; }
  .form-modal h2 { margin: 0 0 20px; color: #1f2937; font-size: 19px; }
  .modal-close { position: absolute; right: 16px; top: 12px; border: 0; background: transparent; color: #64748b; font-size: 24px; cursor: pointer; }
  .form-modal > label { min-height: 46px; display: grid; grid-template-columns: 100px 1fr; align-items: center; }
  .form-modal > label > span:first-child { text-align: right; padding-right: 12px; }
  .form-modal i { color: #ef4444; font-style: normal; }
  .form-modal input:not([type='checkbox']) { height: 34px; padding: 0 10px; border: 1px solid #d2d8e1; outline-color: #1684ff; box-sizing: border-box; }
  .invoice-modal > label { grid-template-columns: 120px minmax(0, 1fr); }
  .invoice-modal > label > input:not([type='checkbox']) { width: 100%; }
  .default-line { grid-template-columns: 100px auto !important; justify-content: flex-start; gap: 8px; }
  .modal-actions { margin-top: 18px; display: flex; justify-content: flex-end; gap: 12px; }
  .modal-actions button { height: 36px; padding: 0 20px; border: 1px solid #d2d8e1; border-radius: 5px; background: #fff; cursor: pointer; }
  .modal-actions .primary { border-color: #1684ff; background: #1684ff; color: #fff; }
  .invoice-warning { color: #ff4d00; font-size: 13px; }
  .radio-text { color: #1684ff; }
  .member-invoice-modal { width: 520px; padding: 0; overflow: hidden; border-radius: 8px; }
  .member-invoice-modal h2 { margin: 0; padding: 18px 22px; border-bottom: 1px solid #e5e7eb; font-size: 17px; }
  .member-invoice-modal .invoice-warning { margin: 16px 22px 4px; padding: 10px 12px; background: #fff7ed; }
  .member-invoice-modal > label { margin: 0 22px; }
  .member-invoice-modal .modal-actions { margin: 14px 0 0; padding: 14px 22px; border-top: 1px solid #e5e7eb; }
  .invoice-type-options { display: flex; align-items: center; gap: 24px; }
  .invoice-type-options label { cursor: pointer; }
  .payment-modal { text-align: center; }
  .qr-placeholder { min-height: 150px; padding: 20px; border: 1px dashed #1684ff; display: flex; align-items: center; justify-content: center; background: #f8fbff; word-break: break-all; }
  .qr-placeholder img { width: 220px; height: 220px; display: block; }
  .coupon-options { display: flex; flex-wrap: wrap; gap: 10px; }
  .coupon-options button { min-height: 38px; padding: 0 14px; border: 1px solid #d8dee8; border-radius: 4px; background: #fff; cursor: pointer; }
  .coupon-options button.active { border-color: #1684ff; color: #1684ff; background: #eef7ff; }
  .empty-checkout { padding: 150px 0; border-radius: 10px; background: #fff; text-align: center; }
  .empty-checkout button { float: none; width: 150px; height: 40px; margin-top: 14px; font-size: 15px; }
</style>
